/**
 * CapabilityCard — Type D. Replaces every "no data yet" empty state.
 *
 * Says what the page does, what it saves the user, and offers exactly one
 * primary action. Purely presentational: render it when your data is empty.
 */

import React from 'react';
import type { LucideIcon } from 'lucide-react';

export interface CapabilityAction {
  label: string;
  onClick?: () => void;
}

interface Props {
  icon?: LucideIcon;
  /** Tint for the icon tile. Default primary. */
  tone?: 'primary' | 'navy' | 'success';
  title: string;
  body: string;
  primary?: CapabilityAction;
  secondary?: CapabilityAction;
  footnote?: string;
  /** Optional supporting steps — used on the Campaigns hero. */
  steps?: { label: string; title: string; body: string }[];
  /** Clickable starter prompts that pre-fill an input. */
  chips?: { label: string; onClick: () => void }[];
  chipsLabel?: string;
  className?: string;
}

const TONE: Record<
  NonNullable<Props['tone']>,
  { tile: string; icon: string }
> = {
  primary: { tile: 'bg-primary/10', icon: 'text-primary' },
  navy: { tile: 'bg-[#0F2C63]/10', icon: 'text-[#0F2C63]' },
  success: { tile: 'bg-[#0F9D6B]/10', icon: 'text-[#0F9D6B]' },
};

export const CapabilityCard: React.FC<Props> = ({
  icon: Icon,
  tone = 'primary',
  title,
  body,
  primary,
  secondary,
  footnote,
  steps,
  chips,
  chipsLabel = 'OR START FROM ONE OF THESE',
  className = '',
}) => {
  const t = TONE[tone];
  return (
    <div className={`text-left ${className}`}>
      {Icon && (
        <div
          className={`flex h-9 w-9 items-center justify-center rounded-lg ${t.tile}`}
        >
          <Icon className={`h-[18px] w-[18px] ${t.icon}`} />
        </div>
      )}
      <h3 className="mt-3 text-base font-bold tracking-tight text-ink">
        {title}
      </h3>
      <p className="mt-1.5 max-w-xl text-[13px] leading-relaxed text-ink-3">
        {body}
      </p>

      {steps && steps.length > 0 && (
        <div className="mt-5 grid gap-2.5 sm:grid-cols-3">
          {steps.map((s) => (
            <div
              key={s.label}
              className="rounded-xl border border-line bg-card p-3 dark:border-[#2A2F3D] dark:bg-[#232734]"
            >
              <div className="text-[11px] font-bold tracking-wider text-primary">
                {s.label}
              </div>
              <div className="mt-1.5 text-[13px] font-bold text-ink">
                {s.title}
              </div>
              <p className="mt-1 text-[11.5px] leading-relaxed text-ink-3">
                {s.body}
              </p>
            </div>
          ))}
        </div>
      )}

      {(primary || secondary) && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {primary && (
            <button
              type="button"
              onClick={primary.onClick}
              className="rounded-lg bg-primary px-3.5 py-2 text-xs font-bold text-white transition-colors hover:bg-primaryHover"
            >
              {primary.label}
            </button>
          )}
          {secondary && (
            <button
              type="button"
              onClick={secondary.onClick}
              className="rounded-lg border border-line px-3.5 py-2 text-xs font-semibold text-ink-2 transition-colors hover:border-line-2 hover:text-ink dark:border-[#2A2F3D]"
            >
              {secondary.label}
            </button>
          )}
        </div>
      )}

      {chips && chips.length > 0 && (
        <>
          <div className="mt-6 text-[11.5px] font-bold tracking-widest text-ink-3">
            {chipsLabel}
          </div>
          <div className="mt-2.5 flex flex-wrap gap-2">
            {chips.map((c) => (
              <button
                key={c.label}
                type="button"
                onClick={c.onClick}
                className="rounded-full border border-line bg-card px-3.5 py-2 text-[12.5px] text-ink transition-colors hover:border-primary hover:text-primary dark:border-[#2A2F3D] dark:bg-[#232734]"
              >
                {c.label}
              </button>
            ))}
          </div>
        </>
      )}

      {footnote && (
        <p className="mt-3 text-[11.5px] text-ink-3">{footnote}</p>
      )}
    </div>
  );
};

export default CapabilityCard;
